import { useLiveQuery } from 'dexie-react-hooks';
import { Bot, Check, Copy, Download, FlaskConical, FolderGit2, FolderOpen, History, Loader2, Lock, MonitorDown, Moon, Sun, Upload, X } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { BRAVE_FLAG, folderBrowser } from '../lib/browser';
import { cn } from '../lib/cn';
import { db, recentFolders, rememberFolder, type RecentFolder } from '../lib/db';
import { useTheme } from '../lib/theme';
import { ensureReadPermission, LocalFolderSource, supportsFolderAccess } from '../sources/localFolder';
import { SampleSource } from '../sources/sample';
import { installApp, usePwa } from '../state/pwa';
import { UploadSource } from '../sources/upload';
import { openSource, useWorkspace } from '../state/workspace';

function timeAgo(ms: number): string {
  const s = Math.round((Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString();
}

/** What keeping a folder open gives you, said once for every browser. */
const BENEFIT = 'remembers your folders and shows changes as they happen';

/** Why this browser only reads copies of folders, and how to get more: a flag in Brave, another browser elsewhere. */
function FolderHint() {
  const [copied, setCopied] = useState(false);
  const browser = folderBrowser();
  if (browser === 'brave') {
    return (
      <p className="text-[12px] text-muted" data-testid="folder-hint">
        Brave turns off folder access. Turn on{' '}
        <button
          type="button"
          onClick={() => void navigator.clipboard?.writeText(BRAVE_FLAG).then(() => setCopied(true))}
          className="inline-flex items-center gap-1 rounded bg-panel-2 px-1 font-mono text-[11.5px] text-text hover:bg-hover"
          title="Copy, then paste it in the address bar"
        >
          {BRAVE_FLAG} {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>{' '}
        and restart Brave, and modelviewer {BENEFIT}.
      </p>
    );
  }
  const name = { firefox: 'Firefox', safari: 'Safari', phone: 'A phone or tablet', other: 'This browser' }[browser];
  const where = browser === 'phone' ? 'On a computer, in Chrome or Edge,' : 'In Chrome or Edge,';
  return (
    <p className="text-[12px] text-muted" data-testid="folder-hint">
      {name} can only read a one-time copy of a folder. {where} modelviewer {BENEFIT}.
    </p>
  );
}

export function StartPage() {
  const liveFolders = supportsFolderAccess();
  const recents = useLiveQuery(recentFolders, [], [] as RecentFolder[]);
  const error = useWorkspace((s) => s.error);
  const { theme, toggle } = useTheme();
  const canInstall = usePwa((s) => s.install !== null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const uploadInput = useRef<HTMLInputElement>(null);

  const run = async (task: () => Promise<void>) => {
    setMessage(null);
    setBusy(true);
    try {
      await task();
    } catch (e) {
      if ((e as DOMException).name !== 'AbortError') setMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openHandle = async (handle: FileSystemDirectoryHandle) => {
    const folder = await rememberFolder(handle);
    await openSource(new LocalFolderSource(folder.id, handle));
  };

  const pickFolder = () =>
    run(async () => {
      const handle = await window.showDirectoryPicker!({ id: 'ef-repository', mode: 'read' });
      await openHandle(handle);
    });

  const reopen = (folder: RecentFolder) =>
    run(async () => {
      if (!(await ensureReadPermission(folder.handle, true))) {
        setMessage(`Permission to read ${folder.name} was not granted.`);
        return;
      }
      await openHandle(folder.handle);
    });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const item = [...e.dataTransfer.items].find((i) => i.kind === 'file');
    if (!item) return;
    // Both calls must happen synchronously inside the drop event.
    const handlePromise = item.getAsFileSystemHandle?.();
    const entry = item.webkitGetAsEntry?.();
    void run(async () => {
      const handle = handlePromise ? await handlePromise : null;
      if (handle?.kind === 'directory') {
        await openHandle(handle as FileSystemDirectoryHandle);
      } else if (entry?.isDirectory) {
        await openSource(await UploadSource.fromEntry(entry as FileSystemDirectoryEntry));
      } else {
        setMessage('Drop a folder, not a single file.');
      }
    });
  };

  return (
    <div
      className={cn('relative flex h-full flex-col overflow-auto bg-bg', dragging && 'ring-2 ring-accent ring-inset')}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <div className="absolute top-4 right-4 flex items-center gap-1">
        {canInstall && (
          <button
            onClick={() => void installApp()}
            className="flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] text-muted hover:bg-hover hover:text-text pointer-coarse:h-9"
            title="Install modelviewer as an app. It opens in its own window and works offline."
          >
            <MonitorDown size={15} /> Install app
          </button>
        )}
        <button
          onClick={toggle}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-hover hover:text-text pointer-coarse:h-9 pointer-coarse:w-9"
          aria-label={theme === 'dark' ? 'Use light theme' : 'Use dark theme'}
          title={theme === 'dark' ? 'Use light theme' : 'Use dark theme'}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-6 py-16 max-md:px-5 max-md:py-12">
        <div className="mb-10 flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">modelviewer</h1>
            <p className="text-muted">See how your EF Core model changed across migrations, sketch alternatives, and compare designs.</p>
          </div>
        </div>

        <section className="space-y-3">
          {liveFolders ? (
            <button
              onClick={() => void pickFolder()}
              disabled={busy}
              className="flex w-full items-center gap-3 rounded-lg bg-accent px-4 py-3 text-left font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-60"
            >
              <FolderOpen size={18} className="flex-none" />
              <span className="flex flex-1 flex-wrap items-baseline justify-between gap-x-3">
                <span className="whitespace-nowrap">Open a folder</span>
                <span className="text-[12px] font-normal opacity-80">a repository or project on your computer</span>
              </span>
            </button>
          ) : (
            <>
              <button
                onClick={() => uploadInput.current?.click()}
                disabled={busy}
                className="flex w-full items-center gap-3 rounded-lg bg-accent px-4 py-3 text-left font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-60"
              >
                <Upload size={18} />
                <span className="flex-1">Import a folder</span>
              </button>
              <FolderHint />
            </>
          )}
          <input
            ref={uploadInput}
            type="file"
            className="hidden"
            data-testid="folder-upload"
            // @ts-expect-error webkitdirectory is not in React's attribute types
            webkitdirectory=""
            multiple
            onChange={(e) => {
              const list = e.target.files;
              if (list?.length) void run(() => openSource(UploadSource.fromFileList(list)));
              e.target.value = '';
            }}
          />
          <div className="flex flex-wrap gap-2">
            {liveFolders && (
              <button
                onClick={() => uploadInput.current?.click()}
                disabled={busy}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 whitespace-nowrap text-muted hover:bg-hover hover:text-text pointer-coarse:py-3"
              >
                <Upload size={15} /> Import a copy
              </button>
            )}
            <button
              onClick={() => void run(async () => openSource(await SampleSource.load()))}
              disabled={busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 whitespace-nowrap text-muted hover:bg-hover hover:text-text pointer-coarse:py-3"
            >
              <FlaskConical size={15} /> Try the sample
            </button>
            <button
              disabled
              title="Coming soon"
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border px-3 py-2 whitespace-nowrap text-faint pointer-coarse:py-3"
            >
              <FolderGit2 size={15} /> GitHub repo
            </button>
          </div>
          <p className="text-center text-[12px] text-faint pointer-coarse:hidden">or drop a folder anywhere on this page</p>
        </section>

        {(busy || message || error) && (
          <div className={cn('mt-6 flex items-center gap-2 rounded-md px-3 py-2 text-[12.5px]', busy ? 'text-muted' : 'bg-removed-soft text-removed')}>
            {busy ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Opening…
              </>
            ) : (
              message ?? error
            )}
          </div>
        )}

        {liveFolders && recents.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-2 flex items-center gap-2 text-[11px] font-semibold tracking-wider text-faint uppercase">
              <History size={12} /> Recent
            </h2>
            <ul className="overflow-hidden rounded-lg border border-border">
              {recents.map((f) => (
                <li key={f.id} className="group flex items-center border-b border-border last:border-b-0">
                  <button onClick={() => void reopen(f)} className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left hover:bg-hover">
                    <FolderOpen size={15} className="flex-none text-muted" />
                    <span className="truncate">{f.name}</span>
                    <span className="ml-auto flex-none text-[12px] text-faint">{timeAgo(f.lastOpened)}</span>
                  </button>
                  <button
                    onClick={() => void db.folders.delete(f.id)}
                    className="mr-1 flex h-7 w-7 flex-none items-center justify-center rounded text-faint opacity-0 group-hover:opacity-100 hover:bg-hover hover:text-text focus:opacity-100 pointer-coarse:opacity-100"
                    aria-label={`Remove ${f.name} from recent folders`}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-10 rounded-lg border border-border p-4">
          <h2 className="mb-1 flex items-center gap-2 text-[13px] font-semibold">
            <Bot size={15} className="text-accent" /> Design with your own agent
          </h2>
          <p className="text-[12.5px] leading-relaxed text-muted">
            Run Claude Code, or any agent, in the same folder. With the agent kit it writes designs to <code className="font-mono text-text">.modelviewer/</code>, and they appear here as
            they are written. The app never calls an AI itself.
          </p>
          <a href="/agent-kit.zip" download className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-accent hover:underline">
            <Download size={13} /> Download the agent kit
          </a>
          <p className="mt-0.5 text-[12px] text-faint">
            Unzip it into <code className="font-mono">.claude/skills/</code> in the repository, or in your home folder for every repository.
          </p>
        </section>
      </main>

      <footer className="flex items-center justify-center gap-2 pb-6 text-[12px] text-faint">
        <Lock size={12} /> Everything stays in your browser. Files are read, never uploaded or changed.
      </footer>
    </div>
  );
}
